import { useEffect, useRef, useState, type RefObject } from 'react'

/** True once the browser reports the user prefers reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

interface InViewOptions {
  /** How much of the element must be visible before it counts. */
  threshold?: number
  /** Shrinks the viewport so elements animate slightly before the true edge. */
  rootMargin?: string
  /** Animate every time it enters, rather than once. */
  repeat?: boolean
}

/**
 * Reveals an element the first time it scrolls into view.
 *
 * Returns `true` immediately when reduced motion is requested or when
 * IntersectionObserver is unavailable, so content is never gated behind an
 * animation that will not run.
 */
export function useInView<T extends HTMLElement>(
  options: InViewOptions = {},
): [RefObject<T>, boolean] {
  const { threshold = 0.15, rootMargin = '0px 0px -10% 0px', repeat = false } = options

  const ref = useRef<T>(null)
  const reduced = usePrefersReducedMotion()
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (reduced || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            if (!repeat) observer.unobserve(entry.target)
          } else if (repeat) {
            setInView(false)
          }
        }
      },
      { threshold, rootMargin },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [reduced, threshold, rootMargin, repeat])

  return [ref, inView]
}

/**
 * Normalised scroll progress through the document, 0 → 1.
 * Driven by rAF so a fast scroll never queues a backlog of work.
 */
export function useScrollProgress(): number {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let frame = 0

    const update = () => {
      frame = 0
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      setProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0)
    }

    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  return progress
}
